import { useEffect, useState, useContext, useMemo } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    useTheme,
    TextField,
    Chip,
    Skeleton,
    MenuItem,
    Tooltip,
    FormControl,
    Select,
    Stack,
} from '@mui/material';
import {
    useGetCompetitionEvolutionQuery,
    useGetAvailableDomainsQuery,
    useGetCompetitionEvolutionKpisQuery,
    useGetVisibilityKpisQuery,
    useGetCompetitionEvolutionVisibilityCardQuery,
} from 'src/context/api/competitorsMonitoring/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { FixedSizeList as List } from 'react-window';
import {
    setFilterKeyword,
    setFilterTags,
} from 'src/context/reducers/filter-settings/filterSlice';
import { t } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import countriesJson from 'src/utils/countries.json';
import { CardBar } from 'src/components/visibility/components/CardBar';
import { fDate } from 'src/utils/format-time';
import type { TopCompetitorStat } from 'src/components/visibility/components/TopCompetitorsCard';
import { VisibilityContext } from '../../../../../components/visibility/components/VisibilityProvider';
import Iconify from '../../../../../components/iconify';
import { CountryBar as CountryFilter } from 'src/components/country-bar';
import { useCountryData } from '../../../../../components/visibility/hooks/useCountryData';
import { SearchTermAnalysisHeader } from '../../../../../components/visibility/components/SearchTermAnalysisHeader';
import ChartEvolution from '../../../../../components/visibility/components/ChartEvolution';
import { useGetBrandColorsQuery } from 'src/context/api/brandColor/api';
import InfiniteScrollKeywords from "src/sections/overview/dashboards/views/InfiniteScrollKeywords";
import { dashboardPaths } from 'src/routes/dashboardPaths';
import { HEADER } from 'src/layouts/config-layout';
import GenericInfiniteScrollList from '../generic-infinite-scroll-list';
import { RootState } from 'src/context/store';
import { generateColorFromString } from '../tag-manager/datagrid/add-tag-cell';
import InfiniteScrollKeywordCompetitors from '../InfiniteScrollKeywordCompetitors';
import CustomTooltip from 'src/components/visibility/components/CustomTooltip';

const formatRate = (raw: number | null | undefined) => {
    const value = raw ?? 0;
    if (value > 0 && value < 1) return '<1%';
    return `${Math.round(value)}%`;
};

const getDomainRateValues = (
    domain: any,
    selected: 'paid' | 'blended' | 'winrate',
    isAbsMode: boolean
) => {
    if (selected === 'paid') {
        return {
            current: isAbsMode
                ? domain?.abs_top_paid_presence_rate
                : domain?.top_paid_presence_rate,

            compare: isAbsMode
                ? domain?.abs_top_paid_presence_rate_compare
                : domain?.top_paid_presence_rate_compare,
        };
    }

    if (selected === 'blended') {
        return {
            current: isAbsMode
                ? domain?.abs_top_blended_presence_rate
                : domain?.top_blended_presence_rate,

            compare: isAbsMode
                ? domain?.abs_top_blended_presence_rate_compare
                : domain?.top_blended_presence_rate_compare,
        };
    }

    return {
        current: isAbsMode
            ? domain?.abs_top_competitor_win_rate
            : domain?.top_competitor_win_rate,

        compare: isAbsMode
            ? domain?.abs_top_competitor_win_rate_compare
            : domain?.top_competitor_win_rate_compare,
    };
};

const DomainRate = ({
    domain,
    selected,
    isAbsMode,
    compareEnabled,
}: {
    domain: any;
    selected: 'paid' | 'blended' | 'winrate';
    isAbsMode: boolean;
    compareEnabled: boolean;
}) => {
    const values = getDomainRateValues(
        domain,
        selected,
        isAbsMode
    );

    const currentRate =
        typeof values.current === 'number'
            ? values.current
            : 0;

    const compareRate =
        typeof values.compare === 'number'
            ? values.compare
            : null;

    const differencePp =
        compareEnabled && compareRate !== null
            ? currentRate - compareRate
            : null;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                flexShrink: 0,
                whiteSpace: 'nowrap',
            }}
        >
            <Typography
                variant="body2"
                sx={{
                    color: '#637381',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '22px',
                }}
            >
                {formatRate(currentRate)}
            </Typography>

            {differencePp !== null && (
                <Typography
                    variant="body2"
                    sx={{
                        minWidth: 52,
                        textAlign: 'right',
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '100%',
                        color:
                            differencePp > 0
                                ? '#18A155'
                                : differencePp < 0
                                    ? '#DC1818'
                                    : '#637381',
                    }}
                >
                    {differencePp > 0 ? '+' : ''}
                    {differencePp.toFixed(0)}pp
                </Typography>
            )}
        </Box>
    );
};

export default function CompetitionEvolution() {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    const [isAbsMode, setIsAbsMode] = useState<boolean>(false);
    const [selected, setSelected] = useState<'paid' | 'blended' | 'winrate'>('paid');
    const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
    const [domainFilter, setDomainFilter] = useState('');
    const [hasUserSelected, setHasUserSelected] = useState(false);
    const [hoveredClientDomain, setHoveredClientDomain] = useState<string | null>(null);
    const [hiddenClientDomains, setHiddenClientDomains] = useState<string[]>([]);
    const [graphKeywords, setGraphKeywords] = useState<string[]>([]);
    const dispatch = useDispatch();
    const theme: any = useTheme();
    const { countriesData, isLoading } = useCountryData();
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const ciContext = useContext(VisibilityContext);
    const location = useLocation();
    const navigate = useNavigate();
    const ITEM_HEIGHT = 32;

    const handleKeywordFilterChange = (value: any) => {
        if (filterStore?.keywords) {
            if (filterStore?.keywords.includes(value)) {
                dispatch(setFilterKeyword(filterStore?.keywords.filter((keyword: any) => keyword !== value)))
            } else {
                const updatedKeywords = [...filterStore.keywords, value];
                dispatch(setFilterKeyword(updatedKeywords))
            }
        }
    };

    // Essayer d'utiliser le contexte centralisé si disponible
    const context = useContext(VisibilityContext);

    // --- KPI search-term analysis data for CardBar (same metrics as SearchTermsAnalysis) ---
    const EMPTY_KEYWORDS: string[] = useMemo(() => [], []);
    const isShoppingEnabled = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true;
    const normalizedAdType = (Array.isArray(filterStore?.adType) && filterStore.adType.length === 0)
        ? (isShoppingEnabled ? ['TXT', 'PLA'] : ['TXT'])
        : filterStore?.adType;

    const skipStaQuery =
        !filterStore.start ||
        !filterStore.end ||
        !workspace?.id ||
        (!selectedCountry && (!Array.isArray(filterStore.countries) || filterStore.countries.length === 0));

    const {
        data: competitionEvolutionKpis,
        isFetching: isFetchingCompetitionEvolutionKpis,
    } = useGetCompetitionEvolutionKpisQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, 'yyyy-MM-dd') : null,
            compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
            compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
            timestep: filterStore.timestep,
            devices: filterStore.devices,
            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            keywords: EMPTY_KEYWORDS,
            adType: normalizedAdType,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    const {
        data: overviewKpisData,
        isFetching: isFetchingOverviewKpis,
    } = useGetVisibilityKpisQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, 'yyyy-MM-dd') : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, 'yyyy-MM-dd') : null,
            compareStartDate: filterStore.compareStart !== undefined ? fDate(filterStore.compareStart, 'yyyy-MM-dd') : null,
            compareEndDate: filterStore.compareEnd !== undefined ? fDate(filterStore.compareEnd, 'yyyy-MM-dd') : null,
            compare: filterStore?.compare,

            countries: selectedCountry ? [selectedCountry] : filterStore.countries,
            devices: filterStore.devices,
            keywords: graphKeywords,
            platforms: filterStore.platforms,
            timestep: filterStore.timestep,
            workspaceId: workspace?.id,
        },
        {
            skip: skipStaQuery,
        }
    );

    // Quand l'utilisateur a tout désélectionné, on envoie uniquement le domaine client
    // pour que le backend filtre sur lui seul (sinon il retournerait tous les domaines).
    const apiSelectedDomains =
        hasUserSelected && selectedDomains.length === 0 && workspace?.url
            ? [workspace.url]
            : selectedDomains;

    const { data, isFetching } = useGetCompetitionEvolutionQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            devices: filterStore?.devices,
            startDate: filterStore?.start,
            keywords: graphKeywords,
            endDate: filterStore?.end,
            isAbsMode,
            adType: filterStore?.adType,
            tags: (filterStore.tags || [])
                .map((tag: any) => typeof tag === 'object' ? tag.id : tag)
                .filter((id: any) => id !== undefined && id !== null),
            rateMode: selected,
            timestep: filterStore?.timestep,
            selectedCountry,
            selectedDomains: apiSelectedDomains,
        },
        { skip: !selectedCountry }
    );

    const { data: visibilityCardData, isFetching: isFetchingVisibilityCard } = useGetCompetitionEvolutionVisibilityCardQuery(
        {
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            devices: filterStore.devices || [],
            keywords: graphKeywords,
            platforms: filterStore?.platforms || [],
            workspaceId: workspace?.id,
            selectedCountry,
        },
        { skip: !selectedCountry || !workspace?.id }
    );

    const buttons = [
        {
            key: 'paid',
            label: t('dashboard.global.paid_presence_rate'),
            icon: 'f7:money-dollar-circle',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_paid_presence_rate_abs_top')
                : t('dashboard.global.definition_paid_presence_rate_top'),
        },
        {
            key: 'blended',
            label: t('dashboard.global.blended_presence_rate'),
            icon: 'proicons:globe',
            tooltip: isAbsMode
                ? t('dashboard.global.definition_blended_presence_rate_abs_top')
                : t('dashboard.global.definition_blended_presence_rate_top'),
        },
    ];

    useEffect(() => {
        if (ciContext?.selectedCountry != null) {
            setSelectedCountry(ciContext.selectedCountry);
        }
    }, [ciContext?.selectedCountry]);

    useEffect(() => {
        const params = new URLSearchParams(location.search || (typeof window !== 'undefined' ? window.location.search : ''));
        const codeParam = params.get('country');
        if (!codeParam) return;
        const mappedName = countriesJson?.find((c: any) => String(c.code).toUpperCase() === String(codeParam).toUpperCase())?.name;
        if (mappedName && countriesData.some((c) => c.country_name === mappedName)) {
            setSelectedCountry(mappedName);
            try {
                ciContext?.setSelectedCountry?.(mappedName);
                if (workspace?.id) localStorage.setItem(`ci.selectedCountry.${workspace.id}`, mappedName);
            } catch { }
        }
        const metricParam = params.get('metric');
        if (metricParam === 'paid' || metricParam === 'blended' || metricParam === 'winrate') {
            setSelected(metricParam);
        }
        const domainParam = params.get('domain');
        if (domainParam && domainParam.trim()) {
            setSelectedDomains([domainParam.trim()]);
            setHasUserSelected(true);
        }
    }, [location.search, countriesData, ciContext, workspace?.id]);

    useEffect(() => {
        if (selectedCountry || !countriesData.length || !workspace?.id) return;
        try {
            const key = `ci.selectedCountry.${workspace.id}`;
            const stored = localStorage.getItem(key);
            if (stored && countriesData.some((c) => c.country_name === stored)) {
                setSelectedCountry(stored);
                ciContext?.setSelectedCountry?.(stored);
            }
        } catch { }
    }, [selectedCountry, countriesData, workspace?.id, ciContext]);

    useEffect(() => {
        if (!selectedCountry && countriesData.length > 0) {
            setSelectedCountry(countriesData[0].country_name);
        }
    }, [selectedCountry, countriesData]);

    const { data: availableDomainsData, isFetching: isFetchingAvailableDomains, isLoading: isLoadingAvailableDomains } = useGetAvailableDomainsQuery(
        {
            workspaceId: workspace?.id,
            platforms: filterStore?.platforms,
            tags: (filterStore.tags || [])
                .map((tag: any) => typeof tag === "object" ? tag.id : tag)
                .filter((id: any) => id != null),
            devices: filterStore?.devices,

            compareStartDate:
                filterStore.compareStart !== undefined
                    ? fDate(filterStore.compareStart, 'yyyy-MM-dd')
                    : null,

            compareEndDate:
                filterStore.compareEnd !== undefined
                    ? fDate(filterStore.compareEnd, 'yyyy-MM-dd')
                    : null,

            compare: Boolean(filterStore?.compare),

            startDate: filterStore?.start,
            keywords: graphKeywords,
            endDate: filterStore?.end,
            adType: isShoppingEnabled ? filterStore?.adType : [],
            timestep: filterStore?.timestep,
            selectedCountry: selectedCountry,
        },
        {
            skip: !selectedCountry || !workspace?.id
        }
    );

    const allDomains = availableDomainsData?.data ?? [];
    const graphData = data?.data?.presence_rates_average_metrics_by_date ?? [];

    // Prefetch brand colors dès que availableDomainsData arrive (avant graphData)
    const prefetchDomainNames = useMemo(
        () => Array.from(new Set(allDomains.map((d: any) => d.domain_name).filter(Boolean))),
        [allDomains]
    );
    const allDomainNames: any = useMemo(
        () => Array.from(new Set(graphData.map((d: any) => d.domain_name).filter(Boolean))),
        [graphData]
    );

    const { data: brandColors, isSuccess: brandColorsReady } = useGetBrandColorsQuery(allDomainNames, {
        skip: allDomainNames.length === 0,
    });

    const getDomainColor = useMemo(() => {
        return (domainName: string) => {
            return brandColors?.[domainName] ?? '#ddd';
        };
    }, [brandColors]);

    // --- CardBar metrics: pressure pools, top competitors, blended presence weekly series & top metric ---

    // Build "rows" from KPI data (similar to SearchTermsAnalysis, but we only need a subset for aggregates)
    const rows = useMemo(() => (competitionEvolutionKpis as any[] | undefined)?.map((item: any, index: number) => {
        const isNotScrapped = item?.status === 'not scrapped';
        return {
            id: `${item?.country_name ?? 'NA'}__${item?.search_term ?? index}`,
            searchTerm: item?.search_term ?? '',
            country: item?.country_name ?? '',
            trafficShare: item?.traffic_share ?? 0,
            levelRaw: isNotScrapped ? null : (isAbsMode ? item?.abs_level : item?.level),
            blendedPresenceRate: isNotScrapped
                ? null
                : (isAbsMode ? (item?.abs_top_blended_presence_rate ?? 0) : (item?.blended_presence_rate ?? 0)),
            top_5_competitors: Array.isArray(item?.top_5_competitors) ? item.top_5_competitors : [],
        };
    }) ?? [], [competitionEvolutionKpis, isAbsMode]);

    // Solo bidder rate card: main KPI from total_weekly_solo_bidder_rate (visibility card API)
    const soloBidderValue = useMemo(() => {
        const v = visibilityCardData?.data?.total_weekly_solo_bidder_rate;
        return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    }, [visibilityCardData?.data?.total_weekly_solo_bidder_rate]);

    const soloBidderWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (!Array.isArray(series)) return undefined;
        return series.map((d: { week_start?: string; solo_bidder?: number }) => ({
            week_start: d?.week_start ?? '',
            solo_bidder: typeof d?.solo_bidder === 'number' ? d.solo_bidder : 0,
        }));
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top competitors card data (from overview KPIs: top_3_* if present, else derived from top_5_competitors)
    const topCompetitorsData = useMemo<TopCompetitorStat[]>(() => {
        const firstItem = (overviewKpisData as any[] | undefined)?.[0];
        const candidates =
            firstItem?.top_3_competitors
            ?? firstItem?.top_3_compet
            ?? firstItem?.card_top_3_competitors
            ?? [];

        if (Array.isArray(candidates) && candidates.length > 0) {
            return candidates
                .map((competitor: any) => ({
                    domain: competitor?.domain_name ?? competitor?.domain ?? '',
                    paidPresenceRate: Number(competitor?.paid_presence_rate ?? competitor?.paidPresenceRate ?? 0),
                }))
                .filter((item: TopCompetitorStat) => item.domain.length > 0);
        }

        // Fallback: derive top competitors from top_5_competitors of all overview rows (same as Overview page)
        const stats = new Map<string, { total: number; count: number }>();
        const workspaceDomains = new Set<string>();
        if (workspace?.url) {
            workspaceDomains.add(workspace.url.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
        }
        if (workspace?.friendlyDomains && Array.isArray(workspace.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => {
                workspaceDomains.add(d.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, ''));
            });
        }
        (overviewKpisData as any[] | undefined)?.forEach((item: any) => {
            if (!Array.isArray(item?.top_5_competitors)) return;
            item.top_5_competitors.forEach((competitor: any) => {
                const domain = competitor?.domain_name;
                const rate = competitor?.paid_presence_rate;
                if (!domain || typeof rate !== 'number' || rate <= 0) return;
                const normalized = domain.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                if (workspaceDomains.has(normalized)) return;
                const current = stats.get(domain) ?? { total: 0, count: 0 };
                current.total += rate;
                current.count += 1;
                stats.set(domain, current);
            });
        });
        return Array.from(stats.entries())
            .map(([domain, { total, count }]) => ({
                domain: domain.trim(),
                paidPresenceRate: count ? Math.round((total / count) * 10) / 10 : 0,
            }))
            .filter((item: TopCompetitorStat) => item.domain.length > 0)
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate);
    }, [overviewKpisData, workspace?.url, workspace?.friendlyDomains]);

    // Weekly visibility card series (from cest_card_visibility_rate.sql)
    const blendedPresenceRateWeeklySeries = useMemo(() => {
        const series = visibilityCardData?.data?.visibility_rate_weekly_series;
        if (Array.isArray(series)) {
            return series;
        }
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_weekly_series]);

    // Top metric for visibility card (from cest_card_visibility_rate.sql)
    const blendedPresenceTopMetric = useMemo(() => {
        const value = Number(visibilityCardData?.data?.visibility_rate_total ?? 0);
        if (value > 0) return `${Math.round(value)}%`;
        return undefined;
    }, [visibilityCardData?.data?.visibility_rate_total]);

    const aggregated = useMemo(() => Object.values(
        graphData.reduce((acc: any, row: any) => {
            if (!acc[row.domain_name]) {
                acc[row.domain_name] = { domain_name: row.domain_name, blended: 0, paid: 0, winrate: 0 };
            }
            acc[row.domain_name].blended += (isAbsMode ? row.abs_top_blended_presence_rate : row.top_blended_presence_rate) || 0;
            acc[row.domain_name].paid += (isAbsMode ? row.abs_top_paid_presence_rate : row.top_paid_presence_rate) || 0;
            acc[row.domain_name].winrate += (isAbsMode ? row.abs_top_competitor_win_rate : row.top_competitor_win_rate) || 0;
            return acc;
        }, {})
    ), [graphData, isAbsMode]);

    const presenceMetricKey = useMemo(() => {
        if (selected === 'blended') return 'blended';
        if (selected === 'paid') return 'paid';
        return 'winrate';
    }, [selected]);

    const domainPresenceScores = useMemo(() => {
        const scores = new Map<string, number>();
        aggregated.forEach((item: any) => {
            scores.set(item.domain_name, item[presenceMetricKey] ?? 0);
        });
        return scores;
    }, [aggregated, presenceMetricKey]);

    const clientDomainNames = useMemo(() => {
        const names = new Set<string>();
        if (workspace?.url) names.add(workspace.url);
        if (Array.isArray(workspace?.friendlyDomains)) {
            workspace.friendlyDomains.forEach((d: string) => { if (d) names.add(d); });
        }
        return names;
    }, [workspace?.url, workspace?.friendlyDomains]);

    const topCompetitors = useMemo(() =>
        [...aggregated].filter((d: any) => !clientDomainNames.has(d.domain_name)).sort((a: any, b: any) =>
            selected === 'blended'
                ? b.blended - a.blended
                : selected === 'paid'
                    ? b.paid - a.paid
                    : b.winrate - a.winrate
        ),
        [aggregated, clientDomainNames, selected]);

    const clientDomains = useMemo(() => {
        const metricKey = selected === 'paid' ? 'top_paid_presence_rate' : selected === 'blended' ? 'top_blended_presence_rate' : 'top_competitor_win_rate';
        return Array.from(clientDomainNames)
            .map(name =>
                allDomains.find((d: any) => d.domain_name === name) ??
                { domain_name: name, top_paid_presence_rate: 0, abs_top_paid_presence_rate: 0, top_blended_presence_rate: 0, abs_top_blended_presence_rate: 0, top_competitor_win_rate: 0, abs_top_competitor_win_rate: 0 }
            )
            .sort((a, b) => (b[metricKey] ?? 0) - (a[metricKey] ?? 0));
    }, [clientDomainNames, allDomains, selected]);

    // Phase 1 : domaines dérivés de graphData (chart déjà chargé) en attendant allDomains
    const graphDataDomains = useMemo(() => {
        if (allDomains.length > 0) return [];
        const byDomain = new Map<string, any>();
        for (const row of graphData) {
            const domain = row.domain_name;
            if (!domain || clientDomainNames.has(domain)) continue;
            const rowDate = row.date_group || row.date_day || '';
            const existing = byDomain.get(domain);
            if (!existing || rowDate > existing._date) {
                byDomain.set(domain, {
                    domain_name: domain,
                    top_blended_presence_rate: row.top_blended_presence_rate ?? 0,
                    abs_top_blended_presence_rate: row.abs_top_blended_presence_rate ?? 0,
                    top_paid_presence_rate: row.top_paid_presence_rate ?? 0,
                    abs_top_paid_presence_rate: row.abs_top_paid_presence_rate ?? 0,
                    top_competitor_win_rate: row.top_competitor_win_rate ?? 0,
                    abs_top_competitor_win_rate: row.abs_top_competitor_win_rate ?? 0,
                    _date: rowDate,
                });
            }
        }
        return Array.from(byDomain.values());
    }, [graphData, allDomains, clientDomainNames]);

    const sortedDomains = useMemo(() =>
        (allDomains.length > 0 ? allDomains : graphDataDomains)
            .filter((d: any) => {
                if (!d?.domain_name || clientDomainNames.has(d.domain_name)) return false;
                // Filter out domains with no presence rate to avoid clutter
                const presenceScore = domainPresenceScores.get(d.domain_name) ?? 0;
                return presenceScore > 0;
            })
            .sort(
                (a: any, b: any) =>
                    (domainPresenceScores.get(b.domain_name) ?? 0) -
                    (domainPresenceScores.get(a.domain_name) ?? 0)
            ),
        [allDomains, graphDataDomains, clientDomainNames, domainPresenceScores]);

    useEffect(() => {
        setGraphKeywords(filterStore?.keywords ?? []);
    }, [filterStore?.keywords]);

    useEffect(() => {
        return () => {
            dispatch(setFilterKeyword([]));
        };
    }, [dispatch]);

    const activeDomains = useMemo(
        () =>
            hasUserSelected
                ? selectedDomains
                : topCompetitors.slice(0, 4).map((t: any) => t.domain_name),
        [hasUserSelected, selectedDomains, topCompetitors]
    );

    const Row = ({ index, style, data }: any) => {
        const {
            competitors,
            theme,
            selectedDomains,
            topCompetitors,
            setSelectedDomains,
            selected,
            compareEnabled,
            isAbsMode: isAbsModeRow,
            getDomainColor,
            selectedCountry: rowSelectedCountry,
        } = data;

        const [rowHovered, setRowHovered] = useState(false);
        const d = competitors[index];
        const isActive = activeDomains.includes(d.domain_name);

        const handleClick = () => {
            const effectivePrev = !hasUserSelected
                ? topCompetitors.slice(0, 4).map((t: any) => t.domain_name)
                : selectedDomains;

            if (effectivePrev.includes(d.domain_name)) {
                setHasUserSelected(true);
                setSelectedDomains(effectivePrev.filter((dom: string) => dom !== d.domain_name));
                return;
            }

            if (effectivePrev.length >= 4) {
                enqueueSnackbar(`You can select up to 4 domains only.`, { variant: 'warning' });
                return;
            }

            setHasUserSelected(true);
            setSelectedDomains([...effectivePrev, d.domain_name]);
        };

        const handleAdsShortcut = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setFilterKeyword([]));
            const countryEntry = countriesJson?.find((c: any) => c.name === rowSelectedCountry);
            const code = countryEntry?.code?.toUpperCase() || '';
            navigate(`${dashboardPaths.visibilityAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(d.domain_name)}`);
        };


        return (
            <div style={style} key={d.domain_name}>
                <Box
                    onClick={handleClick}
                    onMouseEnter={() => setRowHovered(true)}
                    onMouseLeave={() => setRowHovered(false)}
                    sx={{
                        cursor: 'pointer',
                        height: ITEM_HEIGHT,
                        opacity: isActive ? 1 : 0.35,
                        borderRadius: '6px',
                        '&:hover': {
                            bgcolor:
                                theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: .5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    bgcolor: getDomainColor(d.domain_name),
                                    flexShrink: 0,
                                }}
                            />


                            <Box
                                sx={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: theme.palette.mode === 'dark' ? '#0E131C' : '#E7EAEE',
                                    flexShrink: 0,
                                }}
                            >
                                <img
                                    src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                    alt={d.domain_name}
                                    style={{ width: 14, height: 14, borderRadius: '50%' }}
                                />
                            </Box>

                            <Typography
                                variant="body2"
                                sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {d.domain_name}
                            </Typography>
                        </Box>

                        {rowHovered ? (
                            <Typography
                                variant="body2"
                                onClick={handleAdsShortcut}
                                sx={{
                                    fontWeight: 500,
                                    flexShrink: 0,
                                    color: '#006C9C',
                                    cursor: 'pointer',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                See ads
                            </Typography>
                        ) : (
                            <DomainRate
                                domain={d}
                                selected={selected}
                                isAbsMode={isAbsModeRow}
                                compareEnabled={compareEnabled}
                            />
                        )}
                    </Box>
                </Box>
            </div>
        );
    };

    const cardBarLoading =
        isFetchingCompetitionEvolutionKpis ||
        isFetchingOverviewKpis ||
        isFetchingVisibilityCard ||
        isLoading ||
        competitionEvolutionKpis === undefined;

    const [hasInitialData, setHasInitialData] = useState(false);

    useEffect(() => {
        setHasInitialData(false);
    }, [workspace?.id]);

    const colorsLoading =
        allDomainNames.length > 0 && !brandColorsReady;

    const pageLoading =
        !selectedCountry ||
        isFetching ||
        isLoadingAvailableDomains ||
        isFetchingAvailableDomains ||
        !data ||
        !availableDomainsData ||
        colorsLoading;

    useEffect(() => {
        if (
            data &&
            availableDomainsData &&
            (!allDomainNames.length || brandColorsReady)
        ) {
            setHasInitialData(true);
        }
    }, [
        data,
        availableDomainsData,
        brandColorsReady,
        allDomainNames.length,
    ]);


    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <CountryFilter
                loading={isLoading}
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={(c) => {
                    setSelectedCountry(c);
                    context?.setSelectedCountry(c);
                }}
                backgroundColor="transparent"
                indicator="radio"
            />

            <Box px={'24px'} mt={isLoading ? '20px' : (countriesData && countriesData?.length > 1) ? '20px' : '0px'}>
                <CardBar
                    blendedPresenceTitle="Visibility Rate"
                    blendedPresenceTopMetric={blendedPresenceTopMetric}
                    blendedPresenceWeeklySeries={blendedPresenceRateWeeklySeries}
                    soloBidderTitle="Solo bidder rate"
                    soloBidderValue={soloBidderValue}
                    soloBidderWeeklySeries={soloBidderWeeklySeries}
                    topCompetitorsTitle="Top Competitors"
                    topCompetitorsData={topCompetitorsData}
                    dateRange="12 weeks"
                    loading={cardBarLoading}
                    selectedCountry={selectedCountry}
                    useNonBrandLayout
                />

                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0,
                        borderRadius: 1,
                        border: '1px solid rgba(255,255,255,0.11)',
                        boxShadow: '0 7px 20px 0 rgba(255,255,255,0.04)',
                        background: 'transparent',
                        overflow: 'hidden',
                        mt: "20px",
                        height: 502
                    }}
                >
                    {/* BODY */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                            border: '1px solid rgba(145, 158, 171, 0.20)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: 'white',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                px: '16px',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                                height: "40px",
                                backgroundColor: 'white',
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                                boxSizing: 'border-box',
                            }}
                        >
                            {/* Filter Buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: "3px" }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        borderRadius: '8px',
                                        border: '1px solid #DFE3E8',
                                        padding: '2px',
                                        gap: '2px',
                                        width: 'fit-content',
                                        backgroundColor: '#FFF',
                                    }}
                                >
                                    {buttons.map((btn: any) => {
                                        const isSelected = selected === btn.key;

                                        return (

                                            <Button
                                                disableElevation
                                                disableRipple
                                                onClick={() => setSelected(btn.key)}
                                                startIcon={
                                                    <Iconify
                                                        icon={btn.icon}
                                                        sx={{
                                                            fontSize: 16,
                                                            color: isSelected ? '#333C47' : '#919EAB',
                                                        }}
                                                    />
                                                }
                                                sx={{
                                                    minWidth: 'auto',
                                                    height: '22px',
                                                    px: '8px',
                                                    py: '3px',
                                                    borderRadius: '6px',
                                                    backgroundColor: isSelected
                                                        ? 'rgba(145, 158, 171, 0.16)'
                                                        : 'transparent',

                                                    '&:hover': {
                                                        backgroundColor: isSelected
                                                            ? 'rgba(145, 158, 171, 0.16)'
                                                            : 'rgba(145, 158, 171, 0.08)',
                                                    },

                                                    '& .MuiButton-startIcon': {
                                                        marginRight: '4px',
                                                        marginLeft: 0,
                                                    },
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        mr: '4px',
                                                        color: isSelected ? '#333C47' : '#919EAB',
                                                        fontFamily: '"Public Sans"',
                                                        fontSize: '13px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '100%',
                                                    }}
                                                >
                                                    {btn.label}
                                                </Typography>

                                                <CustomTooltip
                                                    key={btn.key}
                                                    title={btn.tooltip}
                                                    arrow
                                                    placement="top"
                                                >
                                                    <svg
                                                        style={{ cursor: 'help' }}
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                            stroke={isSelected ? '#637381' : '#919EAB'}
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </CustomTooltip>
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Abs Top Selector */}
                            <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                height="100%"
                            >
                                <GenericInfiniteScrollList
                                    limitTags={1}
                                    sx={{
                                        width: 200,
                                        '& .MuiAutocomplete-inputRoot': {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            padding: '3px !important',
                                        },

                                        '& .MuiInputBase-root': {
                                            alignItems: 'center',
                                        },
                                    }}
                                    fullWidth={true}
                                    searchPlaceholder=""
                                    items={(filterStore as RootState["filters"]).loadedTags}
                                    options={(filterStore as RootState["filters"]).loadedTags}
                                    multiple={true}
                                    value={
                                        filterStore.tags.length === 0
                                            ? [{ id: "__default__", label: "All Clusters" }]
                                            : filterStore.tags
                                    }
                                    onSelectionChange={(selected) => {
                                        const cleaned = selected.filter((item: any) => item.id !== "__default__");
                                        dispatch(
                                            setFilterTags(
                                                cleaned.map((selectedTag: any) =>
                                                    filterStore.loadedTags.find(
                                                        (tag: any) => tag.id === (selectedTag.id || selectedTag)
                                                    )
                                                )
                                            )
                                        );
                                    }}
                                    getOptionLabel={(item: any) => item?.label || (item as any)}
                                    getOptionKey={(item: any) => item.id || (item as any)}
                                    isOptionEqualToValue={(item, value) => (item.id || item) === (value.id || value)}
                                    customItemRenderer={(item) => (
                                        <Chip
                                            label={item.label || (item as any)}
                                            size="small"
                                            variant="soft"
                                            sx={{
                                                backgroundColor: item?.label
                                                    ? generateColorFromString(String(item.label))
                                                    : undefined,
                                                color: "#fff"
                                            }}
                                        />
                                    )}
                                    customTagRenderer={(item) => {
                                        const isDefault = item.id === "__default__";

                                        return (
                                            <Chip
                                                label={item.label}
                                                size="small"
                                                variant={isDefault ? "soft" : "soft"} // style différent
                                                sx={{
                                                    fontSize: '13px',
                                                    backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                                    color: 'rgba(51, 60, 71, 1)',
                                                }}
                                                onDelete={
                                                    isDefault
                                                        ? undefined
                                                        : () => {
                                                            dispatch(
                                                                setFilterTags(
                                                                    filterStore.tags.filter(
                                                                        (tag: any) => tag.id !== item.id
                                                                    ) as any
                                                                )
                                                            );
                                                        }
                                                }
                                            />
                                        );
                                    }}
                                />

                                <Box mr={"8px"} ml={"8px"}>
                                    <InfiniteScrollKeywordCompetitors
                                        keywords={(filterStore?.loadedKeywords || [])}
                                        onKeywordChange={(it: any) => handleKeywordFilterChange(it)}
                                    />
                                </Box>

                                <FormControl size="medium" sx={{ minWidth: 140 }}>
                                    <Select
                                        value={isAbsMode ? 'abs' : 'top'}
                                        onChange={(e) => setIsAbsMode(e.target.value === 'abs')}
                                        displayEmpty
                                        renderValue={(selected) => {
                                            const isAbsoluteTop = selected === 'abs';

                                            return (
                                                <Chip
                                                    label={
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <span>{isAbsoluteTop ? 'Absolute Top' : 'Top'}</span>

                                                            <CustomTooltip
                                                                title={t(
                                                                    isAbsoluteTop
                                                                        ? 'dashboard.visibility.tooltips.absoluteTop'
                                                                        : 'dashboard.visibility.tooltips.top'
                                                                )}
                                                                arrow
                                                                placement="top"
                                                                PopperProps={{
                                                                    style: {
                                                                        zIndex: 9999,
                                                                    },
                                                                }}
                                                            >
                                                                <span
                                                                    onMouseDown={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                    }}
                                                                    style={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        cursor: 'help',
                                                                    }}
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="12"
                                                                        height="12"
                                                                        viewBox="0 0 12 12"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                                            stroke="#637381"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            </CustomTooltip>
                                                        </Stack>
                                                    }
                                                    sx={{
                                                        bgcolor: 'rgba(145, 158, 171, 0.20)',
                                                        color: '#333C47',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(220, 224, 228)',
                                                        },

                                                        fontSize: '13px',
                                                        height: 24,
                                                        borderRadius: '6px',
                                                        '& .MuiChip-label': {
                                                            px: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                        sx={{
                                            py: .5,
                                            borderRadius: '8px',
                                            '& .MuiSelect-select': {
                                                padding: '0 32px 0 4px !important',
                                                display: 'flex',
                                                alignItems: 'center',
                                            },
                                            height: 32,
                                            fontSize: '14px',
                                            '.MuiOutlinedInput-notchedOutline': {
                                                border: '1px solid rgba(145,158,171,0.20)',
                                            },
                                            '.MuiSelect-icon': {
                                                color: theme.palette.mode === 'dark' ? '#fff' : 'black',
                                            },
                                        }}
                                    >
                                        <MenuItem value="top">
                                            <Typography
                                                sx={{
                                                    color: '#333C47',
                                                    fontFamily: 'Public Sans',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    mr: 0.75,
                                                    lineHeight: '22px',
                                                }}
                                            >
                                                Top
                                            </Typography>

                                            <CustomTooltip
                                                title={t('dashboard.visibility.tooltips.top')}
                                                arrow
                                                placement="top"
                                                PopperProps={{
                                                    style: {
                                                        zIndex: 9999,
                                                    },
                                                }}
                                            >
                                                <span
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ display: 'inline-flex', cursor: 'help' }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                            stroke="#637381"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                            </CustomTooltip>
                                        </MenuItem>

                                        <MenuItem value="abs">
                                            <Typography
                                                sx={{
                                                    color: '#333C47',
                                                    fontFamily: 'Public Sans',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    mr: 0.75,
                                                    lineHeight: '22px',
                                                }}
                                            >
                                                Absolute Top
                                            </Typography>

                                            <CustomTooltip
                                                title={t('dashboard.visibility.tooltips.absoluteTop')}
                                                arrow
                                                placement="top"
                                                PopperProps={{
                                                    style: {
                                                        zIndex: 9999,
                                                    },
                                                }}
                                            >
                                                <span
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ display: 'inline-flex', cursor: 'help' }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                            stroke="#637381"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                            </CustomTooltip>
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Chart + Sidebar */}

                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                flex: 1,
                                minHeight: 0,
                                borderTop: '1px solid #E9ECEE',
                                backgroundColor: 'white',
                            }}
                        >
                            {/* Chart */}
                            {pageLoading ? (
                                <Skeleton width="100%" height="540px" variant="rectangular" animation="wave" />
                            ) : (
                                <ChartEvolution
                                    data={data?.data?.presence_rates_average_metrics_by_date}
                                    rateMode={selected}
                                    isAbsMode={isAbsMode}
                                    mainDomain={workspace?.url}
                                    activeDomains={[
                                        ...(workspace?.url && !hiddenClientDomains.includes(workspace.url)
                                            ? [workspace.url]
                                            : []),

                                        ...(Array.isArray(workspace?.friendlyDomains)
                                            ? workspace.friendlyDomains.filter(
                                                (domain: string) =>
                                                    Boolean(domain) && !hiddenClientDomains.includes(domain)
                                            )
                                            : []),

                                        ...(hasUserSelected
                                            ? selectedDomains
                                            : topCompetitors.slice(0, 4).map((t: any) => t.domain_name)),
                                    ]}
                                    getColor={getDomainColor}
                                />
                            )}

                            {/* Sidebar domaines */}
                            {pageLoading
                                ? <Box
                                    sx={{
                                        width: '630px',
                                        px: '20px',
                                        borderLeft:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid #40484f'
                                                : '1px solid #E9ECEE',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#f9f9fa',
                                    }}
                                >
                                    {/* Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#B1B9C4',
                                                fontWeight: 600,
                                                mb: 0.5,
                                                fontSize: '12px',
                                                mt: 1.5,
                                            }}
                                        >
                                            My domain
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#B1B9C4',
                                                fontWeight: 600,
                                                mb: 0.5,
                                                mt: 1.5,
                                            }}
                                        >
                                            {selected === 'blended' ? 'Visibility Rate' : 'Paid Presence Rate'}

                                            <svg style={{ marginLeft: '2px' }} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <g clip-path="url(#clip0_6280_1812)">
                                                    <path d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z" stroke="#637381" stroke-linecap="round" stroke-linejoin="round" />
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_6280_1812">
                                                        <rect width="12" height="12" fill="white" />
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                        </Typography>
                                    </Box>

                                    {/* Domaine client */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: 0.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Skeleton
                                                variant="rounded"
                                                width={10}
                                                height={10}
                                                sx={{ borderRadius: '2px', flexShrink: 0 }}
                                            />

                                            <Skeleton
                                                variant="circular"
                                                width={26}
                                                height={26}
                                                sx={{ flexShrink: 0 }}
                                            />

                                            <Skeleton variant="text" width={120} height={22} />
                                        </Box>

                                        <Skeleton variant="text" width={34} height={22} />
                                    </Box>

                                    {/* Séparation */}
                                    <Box
                                        sx={{
                                            my: 1,
                                            borderBottom:
                                                theme.palette.mode === 'dark'
                                                    ? '1px solid #40484f'
                                                    : '1px solid #E9ECEE',
                                        }}
                                    />

                                    {/* Barre de recherche conservée mais disabled */}
                                    <Box sx={{ py: 1.5 }}>
                                        <TextField
                                            fullWidth
                                            disabled
                                            size="small"
                                            placeholder="Search competitors..."
                                            sx={{
                                                backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                                '& .MuiInputBase-root.Mui-disabled': {
                                                    backgroundColor: 'rgba(145, 158, 171, 0.12)',
                                                },
                                                '& .MuiInputBase-input.Mui-disabled': {
                                                    WebkitTextFillColor: '#919EAB',
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Liste concurrents */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'hidden',
                                            pr: 0.5,
                                        }}
                                    >
                                        {Array.from({ length: 6 }).map((_, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    height: ITEM_HEIGHT,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    py: 0.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <Skeleton
                                                        variant="circular"
                                                        width={10}
                                                        height={10}
                                                        sx={{ flexShrink: 0 }}
                                                    />

                                                    <Skeleton
                                                        variant="circular"
                                                        width={26}
                                                        height={26}
                                                        sx={{ flexShrink: 0 }}
                                                    />

                                                    <Skeleton
                                                        variant="text"
                                                        width={index % 2 === 0 ? 110 : 145}
                                                        height={22}
                                                    />
                                                </Box>

                                                <Skeleton
                                                    variant="text"
                                                    width={index % 3 === 0 ? 32 : 26}
                                                    height={22}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                                :

                                <Box
                                    sx={{
                                        width: "630px",
                                        borderLeft: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        backgroundColor: '#f9f9fa'
                                    }}
                                >
                                    <Box display={"flex"} flexDirection={"row"} justifyContent={"space-between"} px={"20px"}>
                                        <Box display="flex" justifyContent="flex-start" textAlign="left">
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'rgba(145, 158, 171, 0.80)',
                                                    textAlign: 'right',
                                                    fontFamily: '"Public Sans"',
                                                    fontSize: '12px',
                                                    fontStyle: 'normal',
                                                    fontWeight: 500,
                                                    lineHeight: '100%',
                                                    mb: 0.5,
                                                    mt: 1.5,
                                                }}
                                            >
                                                My domain
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                ml: 'auto',
                                                mb: 0.5,
                                                mt: 1.5,
                                            }}
                                        >
                                            <CustomTooltip title={t("dashboard.global.visibility.paidPresenceRate")}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        cursor: 'help',
                                                        color: 'rgba(145, 158, 171, 0.80)',
                                                        fontFamily: '"Public Sans"',
                                                        fontSize: '12px',
                                                        fontStyle: 'normal',
                                                        fontWeight: 500,
                                                        lineHeight: '100%',

                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        gap: '2px',
                                                    }}
                                                >
                                                    {selected === 'blended'
                                                        ? 'Visibility Rate'
                                                        : selected === 'paid'
                                                            ? 'Paid Presence Rate'
                                                            : ''}

                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="12"
                                                        height="12"
                                                        viewBox="0 0 12 12"
                                                        fill="none"
                                                        style={{ flexShrink: 0 }}
                                                    >
                                                        <path
                                                            d="M6 8V6M6 4H6.005M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                                                            stroke="#637381"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </Typography>
                                            </CustomTooltip>
                                        </Box>
                                    </Box>

                                    {/* Bloc fixe : domaines client (url + friendly) + search input */}
                                    {clientDomains.map((d: any) => {
                                        return (
                                            <Box key={d.domain_name} sx={{ flexShrink: 0 }} px={'20px'}>

                                                {/* Client domain + valeur */}
                                                <Box
                                                    onClick={() => {
                                                        setHiddenClientDomains((prev) =>
                                                            prev.includes(d.domain_name)
                                                                ? prev.filter((domain) => domain !== d.domain_name)
                                                                : [...prev, d.domain_name]
                                                        );
                                                    }}
                                                    onMouseEnter={() => setHoveredClientDomain(d.domain_name)}
                                                    onMouseLeave={() => setHoveredClientDomain(null)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 0.5,
                                                        cursor: 'pointer',
                                                        opacity: hiddenClientDomains.includes(d.domain_name) ? 0.35 : 1,
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                        <Box
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                bgcolor: getDomainColor(d.domain_name),
                                                                flexShrink: 0,
                                                            }}
                                                        />

                                                        <Box
                                                            sx={{
                                                                width: 26,
                                                                height: 26,
                                                                borderRadius: '50%',
                                                                overflow: 'hidden',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                bgcolor: "#E7EAEE",

                                                                justifyContent: 'center',
                                                                padding: '2px',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    borderRadius: '50%',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <img
                                                                    src={`https://www.google.com/s2/favicons?domain=${d.domain_name}&sz=32`}
                                                                    alt={d.domain_name}
                                                                    style={{ width: 14, height: 14, borderRadius: '50%' }}
                                                                />
                                                            </Box>
                                                        </Box>

                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {d.domain_name}
                                                        </Typography>
                                                    </Box>

                                                    {hoveredClientDomain === d.domain_name ? (
                                                        <Typography
                                                            variant="body2"
                                                            onClick={(e) => {
                                                                e.stopPropagation();

                                                                dispatch(setFilterKeyword([]));
                                                                const countryEntry = countriesJson?.find(
                                                                    (c: any) => c.name === selectedCountry
                                                                );
                                                                const code = countryEntry?.code?.toUpperCase() || '';

                                                                navigate(
                                                                    `${dashboardPaths.visibilityAds}?country=${encodeURIComponent(code)}&domain=${encodeURIComponent(d.domain_name)}`
                                                                );
                                                            }}
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#006C9C',
                                                                cursor: 'pointer',
                                                                '&:hover': { textDecoration: 'underline' },
                                                            }}
                                                        >
                                                            See ads
                                                        </Typography>
                                                    ) : (
                                                        <DomainRate
                                                            domain={d}
                                                            selected={selected}
                                                            isAbsMode={isAbsMode}
                                                            compareEnabled={Boolean(filterStore?.compare)}
                                                        />
                                                    )}
                                                </Box>

                                            </Box>
                                        );
                                    })}

                                    {/* Search input */}
                                    <Box sx={{ px: 0, my: 1, borderBottom: theme.palette.mode === 'dark' ? '1px solid #40484f' : '1px solid #E9ECEE' }} />

                                    <Box sx={{ py: 1.5 }} px={'20px'}>
                                        <TextField
                                            fullWidth
                                            sx={{ backgroundColor: "rgba(145, 158, 171, 0.12)" }}
                                            size="small"
                                            placeholder="Search competitors..."
                                            value={domainFilter}
                                            onChange={(e) => setDomainFilter(e.target.value)}
                                        />
                                    </Box>

                                    {/* Bloc scrollable : compétiteurs */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'hidden',
                                            pr: '20px',
                                        }}
                                        px={'20px'}
                                    >
                                        {(() => {
                                            const competitors = sortedDomains.filter(
                                                (d: any) =>
                                                    d?.domain_name?.toLowerCase().includes(domainFilter.toLowerCase())
                                            );

                                            if (competitors.length === 0) {
                                                return (
                                                    <Box
                                                        key="no-results"
                                                        sx={{
                                                            px: 2,
                                                            py: 2,
                                                            textAlign: 'center',
                                                            color: theme.palette.text.secondary,
                                                            fontStyle: 'italic',
                                                        }}
                                                    >
                                                        No results
                                                    </Box>
                                                );
                                            }

                                            return (
                                                <Box
                                                    sx={{
                                                        flex: 1,
                                                        minHeight: 0,
                                                        overflow: 'hidden',
                                                        pr: 0.5,
                                                    }}
                                                >
                                                    <List
                                                        height={316}
                                                        itemCount={competitors.length}
                                                        itemSize={ITEM_HEIGHT}
                                                        width="100%"
                                                        style={{ padding: 0, }}
                                                        itemData={{
                                                            competitors,
                                                            theme,
                                                            activeDomains,
                                                            selectedDomains,
                                                            topCompetitors,
                                                            setSelectedDomains,
                                                            selected,
                                                            isAbsMode,
                                                            getDomainColor,
                                                            compareEnabled: Boolean(filterStore?.compare),
                                                            selectedCountry,
                                                        }}
                                                    >
                                                        {Row}
                                                    </List>
                                                </Box>
                                            );
                                        })()}
                                    </Box>

                                </Box>
                            }

                        </Box>
                    </Box>
                </Paper >
            </Box>
        </Box >
    );
}
